import { VALUE_KEY } from 'gcs-common/constants/dropdownSelectConstants';
import {
  getCommunicationAssignmentMainRole,
  getCommunicationAssignmentMemberRole,
  getCommunicationStructure,
  getCommunicationStructureInitialMembers,
  getCommunicationStructureMainMember,
  getCommunicationStructureMemberOptions,
  getCommunicationStructureOptionsForMainRole,
  getCreateCommunicationStructureDefaultErrorMessage,
  getUpdateCommunicationStructureDefaultErrorMessage,
} from 'gcs-common/slices/administration/houseCommunicationStructures/houseCommunicationStructureSelectors';
import {
  resetCreateCommunicationStructureDefaultErrorMessage,
  resetUpdateCommunicationStructureDefaultErrorMessage,
} from 'gcs-common/slices/administration/houseCommunicationStructures/houseCommunicationStructureSlices';
import {
  createHouseCommunicationStructureMembers,
  deleteHouseCommunicationStructure,
  fetchHouseCommunicationStructures,
  updateHouseCommunicationStructureMembers,
} from 'gcs-common/slices/administration/houseCommunicationStructures/houseCommunicationStructureThunks';
import { CommunicationAssignmentType } from 'gcs-common/slices/administration/houseCommunicationStructures/houseCommunicationStructuresConstants';
import { getHasPermission } from 'gcs-common/slices/currentUser/currentUserSelectors';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  showFailureNotification,
  showSuccessNotification,
} from '../../../../../slices/notifications/ShowNotificationThunk';
import RowOpen from '../../../shared/RowOpen/RowOpen';

const CommunicationStructureRowOpen = ({
  houseId,
  isTeamRow = false,
  isEditForm = false,
  communicationStructureId = '',
  setNewCommunicationStructureAssignmentType,
  setEditRowId,
  isModalOpen = false,
  onDeletion,
}) => {
  const validationSchema = Yup.object().shape({
    channelName: isTeamRow && Yup.string().required('Erforderlich'),
    assignmentDirection: Yup.string().nullable().required('Erforderlich'),
    mainRole:
      isTeamRow &&
      Yup.string()
        .nullable()
        .when('channelImage', {
          is: (role) => !role,
          // biome-ignore lint/suspicious/noThenProperty: not an invalide use of Yum object
          then: (schema) =>
            schema.required(
              'Wenn es keinen Hauptkontakt gibt, müssen Sie ein Titelbild auswählen. Das Titelbild wird für alle Teamchats angezeigt.',
            ),
          otherwise: (schema) => schema,
        }),
    communicationStructureMembers: Yup.array()
      .of(Yup.string())
      .nullable()
      .when('mainRole', {
        is: (mainRole) => !mainRole || mainRole.length === 0,
        // biome-ignore lint/suspicious/noThenProperty: not an invalide use of Yum object
        then: (schema) =>
          isTeamRow
            ? schema.required('Entweder Hauptkontakt oder Benutzerdefinierte Kontakte sind erforderlich')
            : schema.required('Mindestens ein Mitarbeiter oder Team ist erforderlich'),
        otherwise: (schema) => schema,
      }),
    channelImage: isTeamRow && Yup.string().nullable(),
  });

  const dispatch = useDispatch();

  const createDefaultErrorMessage = useSelector(getCreateCommunicationStructureDefaultErrorMessage);
  const updateDefaultErrorMessage = useSelector(getUpdateCommunicationStructureDefaultErrorMessage);
  const communicationAssignmentMainRole = useSelector(getCommunicationAssignmentMainRole);
  const communicationAssignmentMemberRole = useSelector(getCommunicationAssignmentMemberRole);

  const communicationStructure = useSelector(getCommunicationStructure(communicationStructureId));

  const mainContactPerson = useSelector(getCommunicationStructureMainMember(communicationStructureId));

  const communicationStructureMemberOptions = useSelector(getCommunicationStructureMemberOptions);

  const communicationStructureMembers = useSelector(getCommunicationStructureInitialMembers(communicationStructureId));

  const mainContactPersonOptions = useSelector(getCommunicationStructureOptionsForMainRole);

  const mainContactPersonId = mainContactPerson?.customerResponsibilityRoleId || mainContactPerson?.agentId || null;

  const communicationStructureMembersIds = communicationStructureMembers.map((member) => member[VALUE_KEY]);

  const hasGlobalAdminPermission = useSelector(getHasPermission('global_admin'));

  const { channelName, channelImage, assignmentDirection } = communicationStructure || {};

  const initialValues = useMemo(() => {
    return {
      channelName: channelName || '',
      channelImage,
      assignmentDirection,
      mainRole: mainContactPersonId,
      communicationStructureMembers: communicationStructureMembersIds,
    };
  }, [channelName, channelImage, assignmentDirection, mainContactPersonId, communicationStructureMembersIds]);

  useEffect(() => {
    if (createDefaultErrorMessage || updateDefaultErrorMessage) {
      dispatch(
        showFailureNotification(
          createDefaultErrorMessage || updateDefaultErrorMessage,
          (() => {
            dispatch(resetCreateCommunicationStructureDefaultErrorMessage());
            dispatch(resetUpdateCommunicationStructureDefaultErrorMessage());
          })(),
        ),
      );
    }
  }, [createDefaultErrorMessage, updateDefaultErrorMessage, dispatch]);

  useEffect(() => {
    dispatch(resetCreateCommunicationStructureDefaultErrorMessage());
    dispatch(resetUpdateCommunicationStructureDefaultErrorMessage());
  }, [dispatch]);

  const onExpandButtonClick = useCallback(() => {
    setEditRowId(null);
    setNewCommunicationStructureAssignmentType(null);
  }, [setEditRowId, setNewCommunicationStructureAssignmentType]);

  const diff = (a, b) => a.filter((x) => !b.includes(x));

  const memberOptionsHelper = (memberIds, options, roleId) => {
    return memberIds
      .filter((id) => !!id)
      .map((id) => {
        const memberOption = options.find((option) => option[VALUE_KEY] === id);
        return {
          ...memberOption,
          communicationAssignmentRoleId: roleId,
        };
      });
  };

  const submit = useCallback(
    async (values, { setSubmitting }) => {
      const newMainContactPersonId = diff([values.mainRole], [mainContactPersonId]);
      const deletedMainContactPersonId = diff([mainContactPersonId], [values.mainRole]);
      const newMembersIds = diff(values.communicationStructureMembers, communicationStructureMembersIds);
      const deletedMembersIds = diff(communicationStructureMembersIds, values.communicationStructureMembers);

      const toBeAddedMemberOptions = [
        ...memberOptionsHelper(
          newMembersIds,
          communicationStructureMemberOptions,
          communicationAssignmentMemberRole.id,
        ),
        ...memberOptionsHelper(newMainContactPersonId, mainContactPersonOptions, communicationAssignmentMainRole.id),
      ];

      const toBeDeletedMemberOptions = [
        ...memberOptionsHelper(deletedMembersIds, communicationStructureMembers, communicationAssignmentMemberRole.id),
        ...memberOptionsHelper(deletedMainContactPersonId, [mainContactPerson], communicationAssignmentMainRole.id),
      ];

      if (isEditForm) {
        const communicationStructureAttributes = ['channelName', 'channelImage', 'assignmentDirection'];
        const updatedStructureValues = communicationStructureAttributes.reduce((red, attr) => {
          if (values[attr] !== communicationStructure[attr]) {
            red[attr] = values[attr];
          }
          return red;
        }, {});

        const result = await dispatch(
          updateHouseCommunicationStructureMembers({
            id: communicationStructureId,
            structure: {
              ...updatedStructureValues,
              newCommunicationStructureMembers: toBeAddedMemberOptions.map((option) => ({
                customerResponsibilityRoleId: option.customerResponsibilityRoleId,
                communicationAssignmentRoleId: option.communicationAssignmentRoleId,
                friendlyName: option.friendlyName,
                teamId: option.teamId,
                agentId: option.agentId,
              })),
              deletedCommunicationStructureMembers: toBeDeletedMemberOptions.map((option) => ({
                customerResponsibilityRoleId: option.customerResponsibilityRoleId,
                communicationAssignmentRoleId: option.communicationAssignmentRoleId,
                friendlyName: option.friendlyName,
                teamId: option.teamId,
                agentId: option.agentId,
              })),
            },
          }),
        );
        if (!result.error) {
          dispatch(showSuccessNotification('Erfolgreich geändert'));
          setEditRowId(null);
        }
      } else {
        const members = [...(values.teamRoles || []), ...(values.individualRoles || [])].map((role) => ({
          communicationAssignmentRoleId: communicationAssignmentMemberRole.id,
          ...role,
        }));
        values.mainRole &&
          members.push({
            communicationAssignmentRoleId: communicationAssignmentMainRole.id,
            ...values.mainRole,
          });

        const result = await dispatch(
          createHouseCommunicationStructureMembers({
            structure: {
              houseId,
              channelName: values.channelName,
              channelImage: values.channelImage,
              assignmentDirection: values.assignmentDirection,
              assignmentType: isTeamRow ? CommunicationAssignmentType.SALES_SPACE : CommunicationAssignmentType.DIRECT,
              communicationStructureMembers: toBeAddedMemberOptions.map((option) => ({
                customerResponsibilityRoleId: option.customerResponsibilityRoleId,
                communicationAssignmentRoleId: option.communicationAssignmentRoleId,
                friendlyName: option.friendlyName,
                teamId: option.teamId,
                agentId: option.agentId,
              })),
            },
          }),
        );
        if (!result.error) {
          dispatch(showSuccessNotification('Erfolgreich hinzugefügt'));
          setEditRowId(null);
        }
      }

      dispatch(fetchHouseCommunicationStructures({ houseId }));
      setSubmitting(false);
      setNewCommunicationStructureAssignmentType(null);
    },
    [
      isEditForm,
      dispatch,
      houseId,
      setNewCommunicationStructureAssignmentType,
      setEditRowId,
      communicationAssignmentMainRole.id,
      communicationStructureId,
      communicationStructure,
      communicationAssignmentMemberRole.id,
      isTeamRow,
      mainContactPersonId,
      communicationStructureMembersIds,
      mainContactPersonOptions,
      communicationStructureMemberOptions,
      communicationStructureMembers,
      mainContactPerson,
    ],
  );

  const deleteCommunicationStructure = useCallback(
    async (id) => {
      const result = await dispatch(deleteHouseCommunicationStructure({ id }));
      if (!result.error) {
        dispatch(showSuccessNotification('Erfolgreich entfernt'));
        onDeletion();
      } else {
        dispatch(showFailureNotification('Fehler beim Löschen'));
      }
    },
    [dispatch, onDeletion],
  );

  return (
    <RowOpen
      // Formik
      formikInitialValues={initialValues}
      validationSchema={validationSchema}
      // Booleans
      isEditForm={isEditForm}
      isTeamRow={isTeamRow}
      hasAdminPermission={hasGlobalAdminPermission}
      isDeleteEnabled={!isModalOpen}
      isHouseLevel
      // Ids
      communicationStructureId={communicationStructureId}
      // Members
      mainContactPerson={mainContactPerson}
      communicationStructureMembers={communicationStructureMembers}
      mainContactPersonOptions={mainContactPersonOptions}
      memberOptions={communicationStructureMemberOptions}
      // Functions
      onSubmit={submit}
      onExpandButtonClick={onExpandButtonClick}
      onCancel={() => {
        setNewCommunicationStructureAssignmentType(null);
        setEditRowId(null);
      }}
      onDelete={() => {
        setNewCommunicationStructureAssignmentType(null);
        setEditRowId(null);
        deleteCommunicationStructure(communicationStructureId);
      }}
    />
  );
};

CommunicationStructureRowOpen.propTypes = {
  houseId: PropTypes.string.isRequired,
  isTeamRow: PropTypes.bool,
  isEditForm: PropTypes.bool,
  setNewCommunicationStructureAssignmentType: PropTypes.func.isRequired,
  communicationStructureId: PropTypes.string,
  setEditRowId: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool,
  onDeletion: PropTypes.func.isRequired,
};

export default CommunicationStructureRowOpen;
